import { useGuildStore } from './guilds'
import { messageMentionsUser } from '@/lib/muting'
import { mergeAck } from '@/lib/inbox'
/**
 * Друзья, личные сообщения, присутствие и непрочитанные. Заполняется из READY,
 * обновляется событиями шлюза и оптимистично — при собственных действиях.
 */
import { create } from 'zustand'
import { socialApi } from '@/lib/socialApi'
import { MessageFlags, type AttachmentRef, type Call, type Channel, type Message, type Poll, type PollCreate, type PresenceStatus, type PresenceUpdate, type ReactionEmoji, type ReadState, type ReadyPayload, type Relationship, type User, type VoiceState, type StickerItem } from '@/types/api'
import { reactionKey } from '@/lib/emoji'
import { compareIds, lastConfirmed, mergeInitial, mergeResync } from '@/lib/messageMerge'
import { isEphemeral } from '@/lib/interactions'

export { compareIds }

/** Игра в статусе: название и, если игра из Steam, её номер — по нему берётся обложка. */
export interface Activity {
  name: string
  steam?: string
}

/** Игра из присутствия: активность типа 0. Остальные типы нам пока не нужны. */
function gameOf(p: { activities?: { name: string; type: number; steam_app_id?: string }[] }): Activity | null {
  const a = p.activities?.find((x) => x.type === 0 && x.name)
  return a ? { name: a.name, ...(a.steam_app_id ? { steam: a.steam_app_id } : {}) } : null
}

const TYPING_TTL = 10_000

// Удаление может прийти, пока сервер уже подготовил старое окно истории.
// Помним такие ID только до завершения соответствующих запросов.
const historyRequests = new Set<{ channelId: string; deleted: Set<string> }>()
let historySession = 0

interface SocialState {
  currentUserId: string | null
  relationships: Record<string, Relationship>
  presences: Record<string, PresenceStatus>
  /** Во что играет каждый: id пользователя → игра. */
  activities: Record<string, Activity>
  channels: Record<string, Channel>
  messages: Record<string, Message[]> // по возрастанию id
  /**
   * Ответы приложений «только вам» (флаг 64) по каналам. В базу они не пишутся и в историю
   * канала не попадают, поэтому живут отдельно от messages: подгрузка истории и переход
   * к сообщению заменяют список канала, а эти должны остаться до перезагрузки, как у Discord.
   */
  ephemeral: Record<string, Message[]>
  hasOlder: Record<string, boolean>
  loading: Record<string, boolean>
  /**
   * Каналы, где загруженное могло отстать: шлюз переподключился, а события за перерыв сервер не
   * досылает. Значение — последнее сообщение, до которого загруженное шло без пропусков.
   * Открытый чат сразу перечитывает конец истории, остальные — когда их откроют.
   */
  stale: Record<string, string | null>
  readStates: Record<string, ReadState>
  typing: Record<string, Record<string, number>> // channelId → userId → истекает в ms
  /** Идущие звонки в ЛС по id канала и кто в них (channelId → userId → состояние). */
  calls: Record<string, Call>
  callStates: Record<string, Record<string, VoiceState>>

  applyReady: (d: ReadyPayload) => void
  /** Шлюз переподключился: всё загруженное помечается устаревшим (см. stale). */
  markStale: () => void
  applyEvent: (t: string, d: unknown) => void
  /** Присутствие участников серверов из GUILD_CREATE. */
  setPresences: (list: PresenceUpdate[]) => void
  reset: () => void

  loadMessages: (channelId: string, before?: string) => Promise<void>
  /** Загружает окно вокруг сообщения (переход из поиска); список заменяется. */
  loadAround: (channelId: string, messageId: string) => Promise<void>
  sendMessage: (channelId: string, content: string, replyTo?: Message, attachments?: AttachmentRef[]) => Promise<void>
  /** Сообщение из одного стикера (до 3 по API). */
  sendSticker: (channelId: string, sticker: StickerItem) => Promise<void>
  retryMessage: (channelId: string, nonce: string) => Promise<void>
  editMessage: (channelId: string, messageId: string, content: string) => Promise<void>
  /** Скрыть превью ссылок (SUPPRESS_EMBEDS): автор или модератор. */
  suppressEmbeds: (channelId: string, messageId: string) => Promise<void>
  /** Поставить или снять свою реакцию (оптимистично). */
  toggleReaction: (channelId: string, message: Message, emoji: ReactionEmoji) => Promise<void>
  /** Отправить опрос. */
  sendPoll: (channelId: string, poll: PollCreate) => Promise<void>
  /** Отправить голосовое: вложение уже загружено (lib/voiceMessage.uploadVoice). */
  sendVoice: (channelId: string, attachment: AttachmentRef) => Promise<void>
  /** Переслать сообщение в канал или беседу (подпись к пересылке — обычным сообщением следом, как у Discord). */
  forwardMessage: (channelId: string, source: Message) => Promise<void>
  /** Свои голоса в опросе целиком (пусто — снять голос); оптимистично, с откатом. */
  votePoll: (channelId: string, message: Message, answerIds: number[]) => Promise<void>
  /** Подвести итоги своего опроса раньше срока. */
  endPoll: (channelId: string, messageId: string) => Promise<void>
  pinMessage: (channelId: string, messageId: string, pinned: boolean) => Promise<void>
  deleteMessage: (channelId: string, messageId: string) => Promise<void>
  /** «Скрыть» у сообщения «только вам»: убрать из памяти клиента, сервер об этом не знает. */
  dismissEphemeral: (channelId: string, messageId: string) => void
  openDM: (userId: string) => Promise<string>
  closeDM: (channelId: string) => Promise<void>
  createGroup: (recipientIds: string[]) => Promise<string>
  renameGroup: (channelId: string, name: string) => Promise<void>
  addToGroup: (channelId: string, userId: string) => Promise<void>
  removeFromGroup: (channelId: string, userId: string) => Promise<void>
  /** Пометить прочитанным: lastMessageId обязателен для каналов серверов и веток. */
  ack: (channelId: string, lastMessageId?: string) => Promise<void>
  typingPing: (channelId: string) => void
  /** Отклонить входящий звонок (перестать звонить себе). */
  declineCall: (channelId: string) => Promise<void>
  /** Позвонить ещё раз тем, кто не в звонке. */
  ringCall: (channelId: string, recipients?: string[]) => Promise<void>
  requestFriend: (username: string) => Promise<void>
  acceptFriend: (userId: string) => Promise<void>
  removeRelationship: (userId: string) => Promise<void>
  block: (userId: string) => Promise<void>
}

const byId = <T extends { id: string }>(list: T[]): Record<string, T> => Object.fromEntries(list.map((x) => [x.id, x]))

function omit<T>(map: Record<string, T>, key: string): Record<string, T> {
  if (!(key in map)) return map
  const next = { ...map }
  delete next[key]
  return next
}

/** Заменяет одно сообщение в списке канала (если оно загружено). */
function patchMessage(all: Record<string, Message[]>, channelId: string, messageId: string, fn: (m: Message) => Message): Record<string, Message[]> {
  const list = all[channelId]
  if (!list || !list.some((m) => m.id === messageId)) return all
  return { ...all, [channelId]: list.map((m) => (m.id === messageId ? fn(m) : m)) }
}

/** Пересчитывает сводку реакций: delta ±1 к счётчику эмодзи, mine — своя реакция (флаг me). */
function applyReaction(list: Message['reactions'], emoji: ReactionEmoji, delta: 1 | -1, mine: boolean): Message['reactions'] {
  const key = reactionKey(emoji)
  const idx = list.findIndex((r) => reactionKey(r.emoji) === key)
  if (idx === -1) {
    return delta > 0 ? [...list, { emoji, count: 1, me: mine }] : list
  }
  const cur = list[idx]!
  // своя реакция уже учтена оптимистично в toggleReaction — событие шлюза не должно считать её второй раз
  if (mine && delta > 0 === cur.me) return list
  const count = Math.max(0, cur.count + delta)
  if (count === 0) return list.filter((_, i) => i !== idx)
  return list.map((r, i) => (i === idx ? { ...r, count, me: mine ? delta > 0 : r.me } : r))
}

/** Голос в опросе: delta ±1 к счётчику варианта; mine — свой голос (me_voted). Свой голос, уже учтённый
 *  оптимистично в votePoll, событие шлюза второй раз не считает — как у реакций. */
function applyPollVote(poll: Poll, answerId: number, delta: 1 | -1, mine: boolean): Poll {
  const results = poll.results ?? { is_finalized: false, answer_counts: [] }
  const cur = results.answer_counts.find((c) => c.id === answerId)
  if (mine && (cur ? cur.me_voted === delta > 0 : delta < 0)) return poll
  const counts = cur
    ? results.answer_counts.map((c) => (c.id === answerId ? { ...c, count: Math.max(0, c.count + delta), me_voted: mine ? delta > 0 : c.me_voted } : c))
    : delta > 0 ? [...results.answer_counts, { id: answerId, count: 1, me_voted: mine }] : results.answer_counts
  return { ...poll, results: { ...results, answer_counts: counts } }
}

/** MESSAGE_UPDATE приходит всем одинаковым: me_voted там false — свои голоса берём из того, что уже знаем. */
function keepOwnVotes(prev: Message, next: Message): Message {
  const mine = prev.poll?.results?.answer_counts.filter((c) => c.me_voted).map((c) => c.id)
  if (!next.poll?.results || !mine?.length) return next
  return { ...next, poll: { ...next.poll, results: { ...next.poll.results, answer_counts: next.poll.results.answer_counts.map((c) => ({ ...c, me_voted: mine.includes(c.id) })) } } }
}

function insertSorted(list: Message[], msg: Message): Message[] {
  const without = list.filter((m) => m.id !== msg.id && !(msg.nonce && m.nonce === msg.nonce && m.pending))
  const idx = without.findIndex((m) => compareIds(m.id, msg.id) > 0)
  if (idx === -1) return [...without, msg]
  return [...without.slice(0, idx), msg, ...without.slice(idx)]
}

const lastTyping: Record<string, number> = {}

export const useSocialStore = create<SocialState>((set, get) => ({
  currentUserId: null,
  relationships: {},
  presences: {},
  activities: {},
  channels: {},
  messages: {},
  ephemeral: {},
  hasOlder: {},
  loading: {},
  stale: {},
  readStates: {},
  typing: {},
  calls: {},
  callStates: {},

  applyReady: (d) =>
    set({
      currentUserId: d.user.id,
      relationships: byId(d.relationships ?? []),
      presences: Object.fromEntries((d.presences ?? []).map((p) => [p.user.id, p.status])),
      activities: Object.fromEntries((d.presences ?? []).flatMap((p) => { const g = gameOf(p); return g ? [[p.user.id, g] as const] : [] })),
      channels: byId(d.private_channels ?? []),
      readStates: byId(d.read_state ?? []),
      calls: Object.fromEntries((d.calls ?? []).map((c) => [c.channel_id, { ...c, voice_states: undefined }])),
      callStates: Object.fromEntries((d.calls ?? []).map((c) => [c.channel_id, Object.fromEntries((c.voice_states ?? []).map((v) => [v.user_id, v]))])),
    }),

  reset: () => {
    historySession++
    historyRequests.clear()
    set({ currentUserId: null, relationships: {}, presences: {}, activities: {}, channels: {}, messages: {}, ephemeral: {}, hasOlder: {}, loading: {}, stale: {}, readStates: {}, typing: {}, calls: {}, callStates: {} })
  },

  // устаревший с прошлого переподключения канал так и не перечитали — граница остаётся прежней
  markStale: () =>
    set((s) => ({
      stale: Object.fromEntries(Object.entries(s.messages).map(([id, list]) => [id, id in s.stale ? (s.stale[id] ?? null) : lastConfirmed(list)])),
    })),

  setPresences: (list) =>
    set((s) => {
      const presences = { ...s.presences }
      const activities = { ...s.activities }
      for (const p of list) {
        presences[p.user.id] = p.status
        const game = gameOf(p)
        if (game) activities[p.user.id] = game
        else delete activities[p.user.id]
      }
      return { presences, activities }
    }),

  applyEvent: (t, d) => {
    switch (t) {
      case 'MESSAGE_CREATE': {
        const msg = d as Message
        // «только вам» — ни в историю, ни в непрочитанное: это ответ на своё же действие
        if (isEphemeral(msg)) {
          set((s) => ({ ephemeral: { ...s.ephemeral, [msg.channel_id]: insertSorted(s.ephemeral[msg.channel_id] ?? [], msg) } }))
          break
        }
        set((s) => {
          const list = s.messages[msg.channel_id]
          const channel = s.channels[msg.channel_id]
          const next: Partial<SocialState> = {}
          if (list) next.messages = { ...s.messages, [msg.channel_id]: insertSorted(list, msg) }
          if (channel) next.channels = { ...s.channels, [msg.channel_id]: { ...channel, last_message_id: msg.id } }
          const rs = s.readStates[msg.channel_id] ?? { id: msg.channel_id, last_message_id: null, mention_count: 0 }
          const mine = msg.author.id === s.currentUserId
          const guildState = useGuildStore.getState()
          const mentioned = !msg.guild_id || Boolean([0, 19, 20, 23].includes(msg.type) && s.currentUserId && messageMentionsUser(msg, s.currentUserId, guildState.settings[msg.guild_id], guildState.members[msg.guild_id]?.[s.currentUserId]?.roles ?? []))
          next.readStates = {
            ...s.readStates,
            [msg.channel_id]: mine ? { ...rs, last_message_id: msg.id, mention_count: 0 } : { ...rs, mention_count: rs.mention_count + (mentioned ? 1 : 0) },
          }
          const typing = { ...(s.typing[msg.channel_id] ?? {}) }
          delete typing[msg.author.id]
          next.typing = { ...s.typing, [msg.channel_id]: typing }
          return next
        })
        const s = get()
        if (!s.channels[msg.channel_id] && !msg.guild_id) void refreshChannel(msg.channel_id)
        break
      }
      case 'MESSAGE_UPDATE': {
        const msg = d as Message
        set((s) => {
          const next: Partial<SocialState> = {}
          const list = s.messages[msg.channel_id]
          if (list) next.messages = { ...s.messages, [msg.channel_id]: list.map((m) => (m.id === msg.id ? keepOwnVotes(m, { ...m, ...msg }) : m)) }
          // бот дописал ответ «только вам» (снял «думает…») или поменял кнопки под ним
          const eph = s.ephemeral[msg.channel_id]
          if (eph?.some((m) => m.id === msg.id)) next.ephemeral = { ...s.ephemeral, [msg.channel_id]: eph.map((m) => (m.id === msg.id ? { ...m, ...msg } : m)) }
          return next
        })
        break
      }
      case 'MESSAGE_DELETE':
      case 'MESSAGE_DELETE_BULK': {
        const { id, ids, channel_id } = d as { id?: string; ids?: string[]; channel_id: string }
        const removed = new Set(t === 'MESSAGE_DELETE_BULK' ? ids : [id])
        for (const request of historyRequests) {
          if (request.channelId === channel_id) for (const id of removed) if (id) request.deleted.add(id)
        }
        set((s) => {
          const next: Partial<SocialState> = {}
          const list = s.messages[channel_id]
          if (list) next.messages = { ...s.messages, [channel_id]: list.filter((m) => !removed.has(m.id)) }
          const eph = s.ephemeral[channel_id]
          if (eph?.some((m) => removed.has(m.id))) next.ephemeral = { ...s.ephemeral, [channel_id]: eph.filter((m) => !removed.has(m.id)) }
          return next
        })
        break
      }
      case 'MESSAGE_ACK': {
        const { channel_id, message_id, mention_count = 0 } = d as { channel_id: string; message_id: string; mention_count?: number }
        set((s) => ({ readStates: { ...s.readStates, [channel_id]: mergeAck(s.readStates[channel_id], { id: channel_id, last_message_id: message_id, mention_count }) } }))
        break
      }
      case 'TYPING_START': {
        const { channel_id, user_id } = d as { channel_id: string; user_id: string }
        set((s) => ({ typing: { ...s.typing, [channel_id]: { ...(s.typing[channel_id] ?? {}), [user_id]: Date.now() + TYPING_TTL } } }))
        break
      }
      case 'CHANNEL_CREATE': {
        const ch = d as Channel
        if (ch.guild_id) break
        set((s) => ({ channels: { ...s.channels, [ch.id]: ch } }))
        break
      }
      case 'CHANNEL_UPDATE': {
        const ch = d as Channel
        if (ch.guild_id) break
        set((s) => ({ channels: { ...s.channels, [ch.id]: { ...(s.channels[ch.id] ?? {}), ...ch } } }))
        break
      }
      case 'THREAD_DELETE':
      case 'CHANNEL_DELETE': {
        const ch = d as Channel
        set((s) => {
          const channels = { ...s.channels }
          delete channels[ch.id]
          const messages = { ...s.messages }
          delete messages[ch.id]
          return { channels, messages }
        })
        break
      }
      case 'CHANNEL_RECIPIENT_ADD': {
        const { channel_id, user } = d as { channel_id: string; user: User }
        set((s) => {
          const ch = s.channels[channel_id]
          if (!ch || user.id === s.currentUserId) return {}
          const recipients = [...(ch.recipients ?? []).filter((u) => u.id !== user.id), user]
          return { channels: { ...s.channels, [channel_id]: { ...ch, recipients } } }
        })
        break
      }
      case 'CHANNEL_RECIPIENT_REMOVE': {
        const { channel_id, user } = d as { channel_id: string; user: User }
        set((s) => {
          const ch = s.channels[channel_id]
          if (!ch) return {}
          return { channels: { ...s.channels, [channel_id]: { ...ch, recipients: (ch.recipients ?? []).filter((u) => u.id !== user.id) } } }
        })
        break
      }
      case 'RELATIONSHIP_ADD': {
        const rel = d as Relationship
        set((s) => ({ relationships: { ...s.relationships, [rel.id]: rel } }))
        break
      }
      case 'RELATIONSHIP_REMOVE': {
        const { id } = d as { id: string }
        set((s) => {
          const relationships = { ...s.relationships }
          delete relationships[id]
          return { relationships }
        })
        break
      }
      case 'PRESENCE_UPDATE': {
        const p = d as PresenceUpdate
        set((s) => {
          const activities = { ...s.activities }
          const game = gameOf(p)
          if (game) activities[p.user.id] = game
          else delete activities[p.user.id]
          return { presences: { ...s.presences, [p.user.id]: p.status }, activities }
        })
        break
      }
      case 'USER_UPDATE': {
        // чужой профиль (имя, аватар): обновляем в друзьях и получателях ЛС; свой обновляет App в сторе аккаунта
        const u = d as User
        set((s) => {
          const rel = s.relationships[u.id]
          const channels = Object.fromEntries(
            Object.entries(s.channels).map(([id, ch]) => [id, ch.recipients?.some((r) => r.id === u.id) ? { ...ch, recipients: (ch.recipients ?? []).map((r) => (r.id === u.id ? u : r)) } : ch]),
          )
          return { channels, relationships: rel ? { ...s.relationships, [u.id]: { ...rel, user: u } } : s.relationships }
        })
        break
      }
      case 'MESSAGE_REACTION_ADD':
      case 'MESSAGE_REACTION_REMOVE': {
        const ev = d as { user_id: string; channel_id: string; message_id: string; emoji: ReactionEmoji }
        const me = get().currentUserId
        set((s) => ({ messages: patchMessage(s.messages, ev.channel_id, ev.message_id, (m) => ({ ...m, reactions: applyReaction(m.reactions ?? [], ev.emoji, t === 'MESSAGE_REACTION_ADD' ? 1 : -1, ev.user_id === me) })) }))
        break
      }
      case 'MESSAGE_POLL_VOTE_ADD':
      case 'MESSAGE_POLL_VOTE_REMOVE': {
        const ev = d as { user_id: string; channel_id: string; message_id: string; answer_id: number }
        const me = get().currentUserId
        set((s) => ({
          messages: patchMessage(s.messages, ev.channel_id, ev.message_id, (m) =>
            m.poll ? { ...m, poll: applyPollVote(m.poll, Number(ev.answer_id), t === 'MESSAGE_POLL_VOTE_ADD' ? 1 : -1, ev.user_id === me) } : m),
        }))
        break
      }
      case 'MESSAGE_REACTION_REMOVE_ALL': {
        const ev = d as { channel_id: string; message_id: string }
        set((s) => ({ messages: patchMessage(s.messages, ev.channel_id, ev.message_id, (m) => ({ ...m, reactions: [] })) }))
        break
      }
      case 'MESSAGE_REACTION_REMOVE_EMOJI': {
        const ev = d as { channel_id: string; message_id: string; emoji: ReactionEmoji }
        set((s) => ({ messages: patchMessage(s.messages, ev.channel_id, ev.message_id, (m) => ({ ...m, reactions: (m.reactions ?? []).filter((r) => reactionKey(r.emoji) !== reactionKey(ev.emoji)) })) }))
        break
      }
      case 'CHANNEL_PINS_UPDATE': {
        const ev = d as { channel_id: string; last_pin_timestamp: string | null }
        set((s) => (s.channels[ev.channel_id] ? { channels: { ...s.channels, [ev.channel_id]: { ...s.channels[ev.channel_id]!, last_pin_timestamp: ev.last_pin_timestamp } } } : {}))
        break
      }
      case 'CALL_CREATE': {
        const c = d as Call
        set((s) => ({
          calls: { ...s.calls, [c.channel_id]: { ...c, voice_states: undefined } },
          callStates: { ...s.callStates, [c.channel_id]: Object.fromEntries((c.voice_states ?? []).map((v) => [v.user_id, v])) },
        }))
        break
      }
      case 'CALL_UPDATE': {
        const c = d as Call
        set((s) => ({ calls: { ...s.calls, [c.channel_id]: { ...(s.calls[c.channel_id] ?? {}), ...c, voice_states: undefined } } }))
        break
      }
      case 'CALL_DELETE': {
        const { channel_id } = d as { channel_id: string }
        set((s) => {
          const calls = { ...s.calls }
          const callStates = { ...s.callStates }
          delete calls[channel_id]
          delete callStates[channel_id]
          return { calls, callStates }
        })
        break
      }
      case 'VOICE_STATE_UPDATE': {
        const vs = d as VoiceState
        if (vs.guild_id) break // состояния серверов ведёт стор серверов
        set((s) => {
          const callStates: Record<string, Record<string, VoiceState>> = {}
          for (const [cid, users] of Object.entries(s.callStates)) {
            const next = { ...users }
            delete next[vs.user_id]
            callStates[cid] = next
          }
          if (vs.channel_id) callStates[vs.channel_id] = { ...(callStates[vs.channel_id] ?? {}), [vs.user_id]: vs }
          return { callStates }
        })
        break
      }
      default:
        break
    }
  },

  declineCall: async (channelId) => {
    const me = get().currentUserId
    set((s) => {
      const call = s.calls[channelId]
      return call && me ? { calls: { ...s.calls, [channelId]: { ...call, ringing: call.ringing.filter((id) => id !== me) } } } : {}
    })
    await socialApi.stopRinging(channelId).catch(() => undefined)
  },

  ringCall: async (channelId, recipients) => {
    await socialApi.ring(channelId, recipients)
  },

  loadMessages: async (channelId, before) => {
    if (get().loading[channelId]) return
    const request = { channelId, deleted: new Set<string>() }
    const session = historySession
    const userId = get().currentUserId
    historyRequests.add(request)
    set((s) => ({ loading: { ...s.loading, [channelId]: true } }))
    try {
      const page = await socialApi.messages(channelId, before ? { before, limit: 50 } : { limit: 50 })
      if (session !== historySession || userId !== get().currentUserId) return
      const ascending = page.filter((m) => !request.deleted.has(m.id)).reverse()
      const full = page.length === 50
      set((s) => {
        const existing = s.messages[channelId] ?? []
        if (before) {
          const merged = [...ascending, ...existing.filter((m) => !ascending.some((a) => a.id === m.id))]
          return { messages: { ...s.messages, [channelId]: merged }, hasOlder: { ...s.hasOlder, [channelId]: full } }
        }
        const since = s.stale[channelId]
        const { list, keptOlder } = since === undefined ? { list: mergeInitial(ascending, existing), keptOlder: false } : mergeResync(ascending, existing, since, full)
        return {
          messages: { ...s.messages, [channelId]: list },
          hasOlder: { ...s.hasOlder, [channelId]: keptOlder ? (s.hasOlder[channelId] ?? true) : full },
          stale: omit(s.stale, channelId),
        }
      })
    } finally {
      historyRequests.delete(request)
      if (session === historySession && userId === get().currentUserId) set((s) => ({ loading: { ...s.loading, [channelId]: false } }))
    }
  },

  loadAround: async (channelId, messageId) => {
    const request = { channelId, deleted: new Set<string>() }
    const session = historySession
    const userId = get().currentUserId
    historyRequests.add(request)
    set((s) => ({ loading: { ...s.loading, [channelId]: true } }))
    try {
      const page = await socialApi.messagesAround(channelId, messageId)
      if (session !== historySession || userId !== get().currentUserId) return
      const ascending = page.filter((m) => !request.deleted.has(m.id)).sort((a, b) => compareIds(a.id, b.id))
      set((s) => ({ messages: { ...s.messages, [channelId]: ascending }, hasOlder: { ...s.hasOlder, [channelId]: true }, stale: omit(s.stale, channelId) }))
    } finally {
      historyRequests.delete(request)
      if (session === historySession && userId === get().currentUserId) set((s) => ({ loading: { ...s.loading, [channelId]: false } }))
    }
  },

  sendMessage: async (channelId, content, replyTo, attachments) => {
    const me = get().currentUserId
    const nonce = `${Date.now()}${Math.floor(Math.random() * 1e6)}`
    const optimistic: Message = {
      id: `pending:${nonce}`,
      channel_id: channelId,
      author: { id: me ?? '0', username: '', global_name: null, avatar: null },
      content,
      timestamp: new Date().toISOString(),
      edited_timestamp: null,
      mentions: [],
      attachments: [],
      embeds: [],
      reactions: [],
      pinned: false,
      type: replyTo ? 19 : 0,
      nonce,
      pending: true,
      ...(replyTo ? { referenced_message: replyTo, message_reference: { message_id: replyTo.id, channel_id: channelId } } : {}),
    }
    set((s) => ({
      messages: { ...s.messages, [channelId]: [...(s.messages[channelId] ?? []), optimistic] },
      // история канала ещё не загружена (подпись к пересылке в другой канал): при открытии подтянется страница
      ...(s.messages[channelId] ? {} : { stale: { ...s.stale, [channelId]: null } }),
    }))
    try {
      const msg = await socialApi.send(channelId, { content, nonce, ...(replyTo ? { message_reference: { message_id: replyTo.id } } : {}), ...(attachments?.length ? { attachments } : {}) })
      set((s) => ({ messages: { ...s.messages, [channelId]: insertSorted(s.messages[channelId] ?? [], msg) } }))
    } catch (err) {
      set((s) => ({
        messages: { ...s.messages, [channelId]: (s.messages[channelId] ?? []).map((m) => (m.nonce === nonce ? { ...m, pending: false, failed: true } : m)) },
      }))
      throw err
    }
  },

  sendSticker: async (channelId, sticker) => {
    const me = get().currentUserId
    const nonce = `${Date.now()}${Math.floor(Math.random() * 1e6)}`
    const optimistic: Message = {
      id: `pending:${nonce}`, channel_id: channelId, author: { id: me ?? '0', username: '', global_name: null, avatar: null }, content: '',
      timestamp: new Date().toISOString(), edited_timestamp: null, mentions: [], attachments: [], embeds: [], reactions: [], pinned: false, type: 0,
      nonce, pending: true, sticker_items: [sticker],
    }
    set((s) => ({ messages: { ...s.messages, [channelId]: [...(s.messages[channelId] ?? []), optimistic] } }))
    try {
      const msg = await socialApi.send(channelId, { content: '', nonce, sticker_ids: [sticker.id] })
      set((s) => ({ messages: { ...s.messages, [channelId]: insertSorted(s.messages[channelId] ?? [], msg) } }))
    } catch (err) {
      set((s) => ({ messages: { ...s.messages, [channelId]: (s.messages[channelId] ?? []).filter((m) => m.nonce !== nonce) } }))
      throw err
    }
  },

  retryMessage: async (channelId, nonce) => {
    const msg = (get().messages[channelId] ?? []).find((m) => m.nonce === nonce)
    if (!msg) return
    set((s) => ({ messages: { ...s.messages, [channelId]: (s.messages[channelId] ?? []).filter((m) => m.nonce !== nonce) } }))
    await get().sendMessage(channelId, msg.content, msg.referenced_message ?? undefined)
  },

  editMessage: async (channelId, messageId, content) => {
    const msg = await socialApi.edit(channelId, messageId, content)
    set((s) => ({ messages: { ...s.messages, [channelId]: (s.messages[channelId] ?? []).map((m) => (m.id === msg.id ? msg : m)) } }))
  },

  suppressEmbeds: async (channelId, messageId) => {
    const msg = await socialApi.setFlags(channelId, messageId, MessageFlags.SuppressEmbeds)
    set((s) => ({ messages: { ...s.messages, [channelId]: (s.messages[channelId] ?? []).map((m) => (m.id === msg.id ? msg : m)) } }))
  },

  toggleReaction: async (channelId, message, emoji) => {
    const key = reactionKey(emoji)
    const existing = (message.reactions ?? []).find((r) => reactionKey(r.emoji) === key)
    const adding = !existing?.me
    set((s) => ({ messages: patchMessage(s.messages, channelId, message.id, (m) => ({ ...m, reactions: applyReaction(m.reactions ?? [], emoji, adding ? 1 : -1, true) })) }))
    try {
      if (adding) await socialApi.addReaction(channelId, message.id, key)
      else await socialApi.removeReaction(channelId, message.id, key)
    } catch (err) {
      set((s) => ({ messages: patchMessage(s.messages, channelId, message.id, (m) => ({ ...m, reactions: applyReaction(m.reactions ?? [], emoji, adding ? -1 : 1, true) })) }))
      throw err
    }
  },

  sendPoll: async (channelId, poll) => {
    const nonce = `${Date.now()}${Math.floor(Math.random() * 1e6)}`
    const msg = await socialApi.send(channelId, { content: '', nonce, poll })
    set((s) => ({ messages: { ...s.messages, [channelId]: insertSorted(s.messages[channelId] ?? [], msg) } }))
  },

  sendVoice: async (channelId, attachment) => {
    const nonce = `${Date.now()}${Math.floor(Math.random() * 1e6)}`
    const msg = await socialApi.send(channelId, { content: '', nonce, attachments: [attachment], flags: MessageFlags.IsVoiceMessage })
    set((s) => ({ messages: { ...s.messages, [channelId]: insertSorted(s.messages[channelId] ?? [], msg) } }))
  },

  forwardMessage: async (channelId, source) => {
    const nonce = `${Date.now()}${Math.floor(Math.random() * 1e6)}`
    const msg = await socialApi.send(channelId, {
      content: '', nonce,
      message_reference: { type: 1, message_id: source.id, channel_id: source.channel_id, ...(source.guild_id ? { guild_id: source.guild_id } : {}) },
    })
    // как MESSAGE_CREATE: в незагруженный канал не кладём, иначе при открытии не подтянется история
    set((s) => {
      const list = s.messages[channelId]
      return list ? { messages: { ...s.messages, [channelId]: insertSorted(list, msg) } } : {}
    })
  },

  votePoll: async (channelId, message, answerIds) => {
    const had = (message.poll?.results?.answer_counts ?? []).filter((c) => c.me_voted).map((c) => c.id)
    const apply = (forward: boolean) => set((s) => ({
      messages: patchMessage(s.messages, channelId, message.id, (m) => {
        if (!m.poll) return m
        let poll = m.poll
        for (const id of had) if (!answerIds.includes(id)) poll = applyPollVote(poll, id, forward ? -1 : 1, true)
        for (const id of answerIds) if (!had.includes(id)) poll = applyPollVote(poll, id, forward ? 1 : -1, true)
        return { ...m, poll }
      }),
    }))
    apply(true)
    try {
      await socialApi.votePoll(channelId, message.id, answerIds)
    } catch (err) {
      apply(false)
      throw err
    }
  },

  endPoll: async (channelId, messageId) => {
    const msg = await socialApi.endPoll(channelId, messageId)
    set((s) => ({ messages: patchMessage(s.messages, channelId, messageId, (m) => keepOwnVotes(m, { ...m, ...msg })) }))
  },

  pinMessage: async (channelId, messageId, pinned) => {
    if (pinned) await socialApi.pin(channelId, messageId)
    else await socialApi.unpin(channelId, messageId)
  },

  deleteMessage: async (channelId, messageId) => {
    await socialApi.remove(channelId, messageId)
    get().applyEvent('MESSAGE_DELETE', { channel_id: channelId, id: messageId })
  },

  dismissEphemeral: (channelId, messageId) =>
    set((s) => ({ ephemeral: { ...s.ephemeral, [channelId]: (s.ephemeral[channelId] ?? []).filter((m) => m.id !== messageId) } })),

  openDM: async (userId) => {
    const existing = Object.values(get().channels).find((c) => c.type === 1 && c.recipients?.[0]?.id === userId)
    if (existing) return existing.id
    const ch = await socialApi.openDM(userId)
    set((s) => ({ channels: { ...s.channels, [ch.id]: ch } }))
    return ch.id
  },

  closeDM: async (channelId) => {
    await socialApi.closeDM(channelId)
    set((s) => {
      const channels = { ...s.channels }
      delete channels[channelId]
      return { channels }
    })
  },

  createGroup: async (recipientIds) => {
    const ch = await socialApi.createGroup(recipientIds)
    set((s) => ({ channels: { ...s.channels, [ch.id]: ch } }))
    return ch.id
  },

  renameGroup: async (channelId, name) => {
    const ch = await socialApi.updateGroup(channelId, { name })
    set((s) => ({ channels: { ...s.channels, [channelId]: ch } }))
  },

  addToGroup: async (channelId, userId) => {
    await socialApi.addRecipient(channelId, userId)
  },

  removeFromGroup: async (channelId, userId) => {
    await socialApi.removeRecipient(channelId, userId)
  },

  ack: async (channelId, lastMessageId) => {
    const s = get()
    // каналы серверов и ветки живут в другом сторе, поэтому id последнего сообщения
    // передаёт тот, кто его показывает; для ЛС берём из самого канала
    const last = lastMessageId ?? s.channels[channelId]?.last_message_id
    if (!last) return
    const rs = s.readStates[channelId]
    if (rs && rs.last_message_id === last && rs.mention_count === 0) return
    // назад отметку не двигаем: сервер её всё равно не понизит (GREATEST), а на экране
    // канал на ровном месте загорелся бы непрочитанным до следующей перезагрузки
    if (rs?.last_message_id && compareIds(rs.last_message_id, last) > 0) return
    set((st) => ({ readStates: { ...st.readStates, [channelId]: { id: channelId, last_message_id: last, mention_count: 0 } } }))
    try {
      await socialApi.ack(channelId, last)
    } catch {
      /* повторим при следующем сообщении */
    }
  },

  typingPing: (channelId) => {
    const now = Date.now()
    if (now - (lastTyping[channelId] ?? 0) < 4000) return
    lastTyping[channelId] = now
    void socialApi.typing(channelId).catch(() => undefined)
  },

  requestFriend: async (username) => {
    await socialApi.requestFriend(username)
  },
  acceptFriend: async (userId) => {
    await socialApi.acceptFriend(userId)
  },
  removeRelationship: async (userId) => {
    await socialApi.removeRelationship(userId)
    set((s) => {
      const relationships = { ...s.relationships }
      delete relationships[userId]
      return { relationships }
    })
  },
  block: async (userId) => {
    await socialApi.block(userId)
  },
}))

async function refreshChannel(channelId: string): Promise<void> {
  try {
    const channels = await socialApi.dmChannels()
    useSocialStore.setState((s) => ({ channels: { ...s.channels, ...byId(channels) } }))
  } catch {
    /* появится при следующем READY */
  }
  void channelId
}

/** Кто печатает в канале сейчас (кроме меня), с отсевом протухших. Вызывать через useMemo: селектор не должен возвращать новый массив на каждый рендер. */
export function typingUsers(map: Record<string, number> | undefined, currentUserId: string | null): string[] {
  const now = Date.now()
  return Object.entries(map ?? {})
    .filter(([id, until]) => until > now && id !== currentUserId)
    .map(([id]) => id)
}

/** Непрочитанных упоминаний по всем ЛС — бейдж на кнопке «Домой». */
export function totalMentions(state: SocialState): number {
  return Object.values(state.readStates).reduce((n, rs) => n + (state.channels[rs.id] ? rs.mention_count : 0), 0)
}

export function displayName(u: User): string {
  return u.global_name ?? u.username
}

/** Имя беседы: собеседник в ЛС, название группы или перечисление участников. */
export function channelName(ch: Channel): string {
  if (ch.type === 1) {
    const r = ch.recipients?.[0]
    return r ? displayName(r) : 'Личные сообщения'
  }
  if (ch.name) return ch.name
  const names = (ch.recipients ?? []).map(displayName)
  return names.length ? names.join(', ') : 'Группа'
}
